import React, { JSX, useMemo } from "react";

import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { chartTooltipFormatter } from "src/components/Tooltip/helpers/chartTooltipFormatter";
import { MeasuresWithHasData } from "src/types/dashboardDatasetDto";

import SeriesType from "../../../constants/seriesType.enum";
import { toShortNumberFormat } from "../../../lib/number.helpers";
import { MeasuresItemizedItemDto, type MeasuresItemizedDto } from "../../../types/locationChartDto";
import { getUnicodeSymbol, axisFormatter, createCustomEventIcon } from "../helpers/dashboardChart.helper";
import { ChartTooltipFormatterParams } from "../interfaces/DashboardComponentParams";

const ReactECharts = React.lazy(() => import("echarts-for-react"));

function WeatherInfluenceOnFootfallChart({ chartDto }: { chartDto: MeasuresItemizedDto }): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const chartData = useMemo(() => chartDto.data || [], [chartDto.data]) as MeasuresWithHasData<MeasuresItemizedItemDto>[];
  const startDate = useMemo(() => chartDto.currentDateRange.start, [chartDto.currentDateRange]);

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        confine: true,
        formatter: (params: ChartTooltipFormatterParams) => chartTooltipFormatter(params, t, theme, null, undefined, undefined, true),
        axisPointer: {
          type: "shadow",
        },
        borderColor: theme.palette.border.default,
      },
      grid: {
        left: "0%",
        right: "0%",
        top: "8%",
        bottom: "8%",
        containLabel: true,
      },
      xAxis: [
        {
          type: "category",
          axisTick: {
            show: false,
          },
          axisLabel: {
            formatter(value: string) {
              const date = dayjs(value);
              return axisFormatter(startDate, date, t);
            },
            color: theme.palette.secondary.light,
            fontSize: theme.fontSizes.xs,
            fontWeight: theme.typography.fontWeightLight,
          },
        },
        {
          type: "category",
          position: "top",
          axisTick: {
            show: false,
          },
          axisLabel: {
            interval: 0,
            hideOverlap: false,
            formatter(value: string, index: number) {
              const data = chartData[index];
              if (!data) return "";
              const { status, event } = data;
              let result = "";
              if (event) {
                result += `{events|${createCustomEventIcon(event)}}`;
              }
              if (status) {
                if (result) result += "\n\n";
                result += `{weather|${getUnicodeSymbol(status)}}`;
              }
              return result;
            },
            rich: {
              weather: {
                fontFamily: "weathericons",
                fontSize: theme.fontSizes.md,
                lineHeight: 14,
              },
              events: {
                fontFamily: "fontEvents",
                fontSize: theme.fontSizes.md,
                lineHeight: 10,
              },
            },
            color: theme.palette.secondary.light,
          },
          data: chartData.map((e) => e.currentDate),
        },
      ],
      yAxis: [
        {
          type: "value",
          axisLabel: {
            formatter(value: number) {
              return toShortNumberFormat(value);
            },
            color: theme.palette.secondary.light,
            fontSize: theme.fontSizes.xs,
            fontWeight: theme.typography.fontWeightLight,
          },
          splitLine: {
            show: false,
          },
        },
        {
          type: "value",
          axisLabel: {
            formatter(value: number) {
              return `${value}°C`;
            },
            color: theme.palette.secondary.light,
            fontSize: theme.fontSizes.xs,
            fontWeight: theme.typography.fontWeightLight,
          },
        },
      ],
      series: [
        {
          name: t("Footfall"),
          type: SeriesType.Bar,
          barGap: "0%",
          emphasis: {
            focus: "series",
          },
          data: chartData.map((e) => [e.currentDate, e.currentValue, e.pastDate, e.pastValue, e.hasData]),
          itemStyle: {
            color: theme.palette.primary.main,
          },
          yAxisIndex: 0,
        },
        {
          name: "Temp",
          type: SeriesType.Line,
          data: chartData.map((e) => [
            e.currentDate,
            e.temperature,
            e.pastDate,
            e.status,
            e.pastTemperature,
            e.pastStatus,
            e.hasData,
            e.eventDescription,
            e.pastEventDescription,
          ]),
          itemStyle: {
            color: theme.palette.levelColors[0],
          },
          symbol: "none",
          yAxisIndex: 1,
        },
      ],
    }),
    [chartData, t, startDate, theme]
  );

  return <ReactECharts option={option} />;
}

export default WeatherInfluenceOnFootfallChart;
